import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DocsView from "@/shared/components/Docs/DocsView";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import FAQView from "@/shared/components/FAQ";
import UpdatesView from "@/shared/components/Updates/UpdatesView";
import { SidebarInset, SidebarProvider } from "@/shared/components/ui/sidebar";
import { getViewFromPath, type ViewMode } from "@/shared/config/routes";
import { EntriesProvider, useEntries } from "@/shared/contexts/EntriesContext";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { useUpdateNotification } from "@/shared/hooks/useUpdateNotification";
import { TimerProvider } from "@/v2/contexts/TimerProvider";
import ProcessingIndicator from "./components/ProcessingIndicator/ProcessingIndicator";
import Sidebar from "./components/Sidebar/Sidebar";
import CalendarView from "./views/Calendar2View";
import EntriesView from "./views/EntriesView";
import ReportingDashboardView from "./views/ReportingDashboardView";
import SettingsView from "./views/SettingsView";
import SupportView from "./views/SupportView/SupportView";

const viewTitles: Record<ViewMode, string> = {
	entries: "Entries",
	calendar: "Calendar",
	settings: "Settings",
	support: "Support",
	faq: "FAQ",
	updates: "Updates",
	reporting: "Reporting",
	docs: "Docs",
};

function AttorneyPageContent() {
	const location = useLocation();
	const navigate = useNavigate();
	const { hasNewUpdate, dismissUpdate } = useUpdateNotification();
	const { isProcessingEntry, processingApplication } = useEntries();

	const viewMode: ViewMode = useMemo(() => {
		return getViewFromPath(location.pathname) || "entries";
	}, [location.pathname]);

	useDocumentTitle(viewTitles[viewMode]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: intentionally runs once on mount to consume a pending update notification redirect.
	useEffect(() => {
		if (hasNewUpdate) {
			dismissUpdate();
			navigate("/updates", { replace: true });
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const renderView = () => {
		switch (viewMode) {
			case "entries":
				return <EntriesView />;
			case "calendar":
				return <CalendarView />;
			case "settings":
				return <SettingsView />;
			case "support":
				return <SupportView />;
			case "faq":
				return <FAQView />;
			case "updates":
				return <UpdatesView />;
			case "reporting":
				return <ReportingDashboardView />;
			case "docs":
				return <DocsView />;
			default:
				return null;
		}
	};

	return (
		<SidebarProvider>
			<ProcessingIndicator
				show={isProcessingEntry}
				message="Processing entry..."
				application={processingApplication}
			/>
			<Sidebar />
			<SidebarInset className="bg-background overflow-hidden">
				<ErrorBoundary>{renderView()}</ErrorBoundary>
			</SidebarInset>
		</SidebarProvider>
	);
}

export default function AttorneyPage() {
	return (
		<TimerProvider>
			<EntriesProvider>
				<AttorneyPageContent />
			</EntriesProvider>
		</TimerProvider>
	);
}
